"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, CircleCheckBig, GitBranch, TriangleAlert } from "lucide-react";
import { useSWRConfig } from "swr";
import { API_BASE_URL } from "@/lib/config";
import { getViewModeChrome } from "../view-mode-chrome";

interface CreatePlanSuccessProps {
  stepIndex?: number;
  sessionId?: string;
  result?: {
    status?: string;
    error_type?: string;
    error_message?: string;
    stage?: string;
    [key: string]: any;
  };
  viewMode?: string;
}

export function CreatePlanSuccess({ stepIndex = 1, sessionId, result, viewMode}: CreatePlanSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const { mutate } = useSWRConfig();

  // Trigger mutation when component renders to refresh plan data
  useEffect(() => {
    if (sessionId) {
      mutate(`${API_BASE_URL}/strategies/strategy-plan-by-sessionID/${sessionId}`);
    }
  }, [sessionId, mutate]);

  // Check if this is a canceled state
  const isCanceled = result?.status === "canceled" || 
                    (result?.error_message === "User requested stop" && result?.stage === "user_cancel");
  
  // Determine title, subtitle, and icon based on state
  const title = isCanceled ? "Blueprint Canceled" : "Blueprint created successfully";
  const subtitle = isCanceled ? "Stopped by user" : "Ready to execute";
  const IconComponent = isCanceled ? TriangleAlert : CircleCheckBig;
  const iconColor = isCanceled ? "text-yellow-500" : "text-green-500";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            {/* Plan icon on the left */}
            <GitBranch className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`} strokeWidth={2} />

            {/* Text content */}
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>

          {/* Status icon on the right */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <IconComponent className={`h-4 w-4 ${iconColor}`} />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
